// src/pages/storePages/manageStore/ManageCategoryPage.js
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  storeGetCategoryBySlug,
  storeGetCategoriesByParentSlug,
  storeDeleteCategory,
} from "../../../storeAPIs/StoreCategoriesApis";
import {
  storeGetProductsInCategoryExact,
  storeDeleteProduct,
} from "../../../storeAPIs/StoreProductsAPIs";
import NewCategoryDialog from "./NewCategoryDialog";
import "../../../static/storecss/manageStore/shared.css";
import "../../../static/storecss/manageStore/ManageCategoryPage.css";

export default function ManageCategoryPage() {
  const { slug } = useParams();

  const [category, setCategory] = useState(null);
  const [children, setChildren] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dialog state
  const [dialog, setDialog] = useState({
    open: false,
    mode: "create", // "create" | "edit"
    target: null,   // the category being edited, or null when creating
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      storeGetCategoryBySlug(slug),
      storeGetCategoriesByParentSlug(slug),
      storeGetProductsInCategoryExact(slug),
    ])
      .then(([catRes, childRes, prodRes]) => {
        if (cancelled) return;
        setCategory(catRes.data);
        setChildren(childRes.data?.results ?? childRes.data ?? []);
        setProducts(prodRes.data?.results ?? prodRes.data ?? []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Failed to load category.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const handleDeleteCategory = async (childSlug, name) => {
    if (!window.confirm(`Delete category "${name}"?`)) return;
    try {
      await storeDeleteCategory(childSlug);
      setChildren((prev) => prev.filter((c) => c.slug !== childSlug));
    } catch (err) {
      alert(
        err.response?.status === 409
          ? "Cannot delete: this category has children or products."
          : err.message || "Failed to delete."
      );
    }
  };

  const handleDeleteProduct = async (productSlug, name) => {
    if (!window.confirm(`Delete product "${name}"? This can't be undone.`))
      return;
    try {
      await storeDeleteProduct(productSlug);
      setProducts((prev) => prev.filter((p) => p.slug !== productSlug));
    } catch (err) {
      alert(err.message || "Failed to delete.");
    }
  };

  // ── Dialog helpers ──────────────────────────────────────
  const openCreateChild = () =>
    setDialog({ open: true, mode: "create", target: null });

  const openEditCategory = (target) =>
    setDialog({ open: true, mode: "edit", target });

  const closeDialog = () =>
    setDialog((d) => ({ ...d, open: false, target: null }));

  const handleSaved = (saved, mode) => {
    if (!saved) return;

    if (mode === "created" && saved.id) {
      setChildren((prev) => [
        ...prev,
        { ...saved, product_count: saved.product_count ?? 0 },
      ]);
    } else if (mode === "updated" && saved.id) {
      if (category && saved.id === category.id) {
        setCategory((prev) => ({ ...prev, ...saved }));
      }
      setChildren((prev) =>
        prev.map((c) => (c.id === saved.id ? { ...c, ...saved } : c))
      );
    }
  };

  if (loading) {
    return (
      <div className="manage-store">
        <p className="manage-loading">Loading…</p>
      </div>
    );
  }

  if (error || !category) {
    return (
      <div className="manage-store">
        <p className="manage-error">{error || "Category not found."}</p>
        <Link to="/manage-store" className="btn-secondary">
          Back to Manage Store
        </Link>
      </div>
    );
  }

  const ancestors = category.ancestors ?? [];
  const thisCategoryPath = `/manage-store/category/${category.slug}`;

  return (
    <div className="manage-store">
      <nav className="manage-breadcrumb">
        <Link to="/manage-store">Manage Store</Link>
        {ancestors.map((a) => (
          <span key={a.id}>
            <span className="sep"> / </span>
            <Link to={`/manage-store/category/${a.slug}`}>{a.name}</Link>
          </span>
        ))}
        <span className="sep"> / </span>
        <span className="current">{category.name}</span>
      </nav>

      <header className="manage-header">
        <div>
          <h1>{category.name}</h1>
          <p className="manage-subtitle">
            <code>{category.slug}</code>
          </p>
        </div>
        <div className="manage-header-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => openEditCategory(category)}
          >
            <i className="fa-solid fa-pen" /> Edit Category
          </button>
        </div>
      </header>

      {/* ── Subcategories ──────────────────────────────────── */}
      <details className="manage-dropdown manage-dropdown--categories">
        <summary className="manage-dropdown-summary">
          <span className="manage-dropdown-title">
            <i className="fa-solid fa-folder-tree" />
            Subcategories
            <span className="manage-dropdown-count">{children.length}</span>
          </span>
          <button
            type="button"
            className="btn-primary btn-small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openCreateChild();
            }}
          >
            <i className="fa-solid fa-plus" /> Add
          </button>
        </summary>

        <div className="manage-dropdown-body">
          {children.length === 0 ? (
            <p className="manage-empty-inline">No subcategories.</p>
          ) : (
            <ul className="manage-list">
              {children.map((child) => (
                <li key={child.id} className="manage-list-row">
                  <Link
                    to={`/manage-store/category/${child.slug}`}
                    className="manage-list-main"
                  >
                    <span className="manage-list-name">
                      <i className="fa-solid fa-folder" />
                      {child.name}
                    </span>
                    <code className="manage-list-slug">{child.slug}</code>
                    <span className="manage-list-meta">
                      {child.product_count ?? 0} products
                    </span>
                  </Link>
                  <div className="manage-list-actions">
                    <button
                      type="button"
                      className="btn-icon"
                      title="Edit"
                      onClick={() => openEditCategory(child)}
                    >
                      <i className="fa-solid fa-pen" />
                    </button>
                    <button
                      className="btn-icon btn-danger"
                      title="Delete"
                      onClick={() =>
                        handleDeleteCategory(child.slug, child.name)
                      }
                    >
                      <i className="fa-solid fa-trash" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </details>

      {/* ── Products ───────────────────────────────────────── */}
      <details className="manage-dropdown manage-dropdown--products">
        <summary className="manage-dropdown-summary">
          <span className="manage-dropdown-title">
            <i className="fa-solid fa-box" />
            Products
            <span className="manage-dropdown-count">{products.length}</span>
          </span>
          <Link
            to={`/manage-store/product/new?category=${
              category.id
            }&returnTo=${encodeURIComponent(thisCategoryPath)}`}
            className="btn-primary btn-small"
            onClick={(e) => e.stopPropagation()}
          >
            <i className="fa-solid fa-plus" /> Add
          </Link>
        </summary>

        <div className="manage-dropdown-body">
          {products.length === 0 ? (
            <p className="manage-empty-inline">No products in this category.</p>
          ) : (
            <ul className="manage-list">
              {products.map((p) => (
                <li key={p.id} className="manage-list-row">
                  <div className="manage-list-main">
                    {p.primary_image ? (
                      <img
                        src={p.primary_image}
                        alt={p.name}
                        className="manage-list-thumb"
                      />
                    ) : (
                      <span className="manage-list-thumb manage-list-thumb-empty">
                        <i className="fa-solid fa-image" />
                      </span>
                    )}
                    <span className="manage-list-name">{p.name}</span>
                    <span className="manage-list-price">
                      KES {Number(p.price).toLocaleString()}
                    </span>
                    <span className="manage-list-stock">
                      {p.stock > 0 ? (
                        `${p.stock} in stock`
                      ) : (
                        <span className="text-muted">Out of stock</span>
                      )}
                    </span>
                    <span
                      className={`manage-pill ${
                        p.is_active
                          ? "manage-pill-active"
                          : "manage-pill-inactive"
                      }`}
                    >
                      {p.is_active ? "Active" : "Hidden"}
                    </span>
                  </div>
                  <div className="manage-list-actions">
                    <Link
                      to={`/store/product/${p.slug}`}
                      className="btn-icon"
                      title="View on storefront"
                    >
                      <i className="fa-solid fa-eye" />
                    </Link>
                    <Link
                      to={`/manage-store/product/${
                        p.slug
                      }/edit?returnTo=${encodeURIComponent(thisCategoryPath)}`}
                      className="btn-icon"
                      title="Edit"
                    >
                      <i className="fa-solid fa-pen" />
                    </Link>
                    <button
                      className="btn-icon btn-danger"
                      title="Delete"
                      onClick={() => handleDeleteProduct(p.slug, p.name)}
                    >
                      <i className="fa-solid fa-trash" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </details>

      {/* ── The dialog itself ─────────────────────────────── */}
      <NewCategoryDialog
        open={dialog.open}
        category={dialog.mode === "edit" ? dialog.target : null}
        parentId={dialog.mode === "create" ? category.id : null}
        parentName={dialog.mode === "create" ? category.name : ""}
        onClose={closeDialog}
        onSaved={handleSaved}
      />
    </div>
  );
}